import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

interface ScrollToTopOptions {
  delay?: number
  smooth?: boolean
  ignorePaths?: string[]
}

const useScrollToTop = (options: ScrollToTopOptions = {}): void => {
  const { delay = 0, smooth = false, ignorePaths = [] } = options
  const { pathname } = useLocation()
  const prevPathnameRef = useRef<string>(pathname)

  useEffect(() => {
    // Only scroll if the pathname has changed
    if (prevPathnameRef.current === pathname) {
      return
    }

    // Update the previous pathname reference
    prevPathnameRef.current = pathname

    // Check if current path should be ignored
    if (ignorePaths.some((path) => pathname.includes(path))) {
      return
    }

    const scrollToTop = (): void => {
      // For newer browsers supporting scrollTo with options
      if ('scrollBehavior' in document.documentElement.style && smooth) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        })
      } else {
        // For older browsers or instant scrolling
        window.scrollTo(0, 0)
      }
    }

    // Execute with or without delay
    if (delay > 0) {
      const timeoutId = setTimeout(scrollToTop, delay)
      return () => clearTimeout(timeoutId)
    } else {
      scrollToTop()
      return undefined // Explicit return for consistent function signature
    }
  }, [pathname, delay, smooth, ignorePaths])
}

export default useScrollToTop
